import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  AlertCircle,
  Building2,
  ContactRound,
  LoaderCircle,
  Search,
  UserRoundCog,
  X,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AccountSearchResultSchema,
  ContactSearchResultSchema,
  FlowSearchResponseSchema,
  type AccountSearchResult,
  type ContactSearchResult,
  type FlowSearchResponse,
  type FlowSearchScope,
} from '@shared/flow/search';
import { AccountsResponseSchema } from '@shared/flow/account-view';
import { ContactsResponseSchema } from '@shared/flow/contact-view';
import { USER_TYPE_INFO } from '@shared/user-types';
import {
  accountInCommandCenter,
  contactInCommandCenter,
  contactsInCommandCenter,
} from '../../../../../mini-apps/_shared/flow-links';
import { AdminMeResponseSchema, type DemoOption, type OwnerOption } from '@shared/schemas/admin-access';
import { fetchAdminMe } from '../../api/admin';
import { useUserControlsContext } from '../../contexts/UserControlsContext';
import {
  isDelphiRoute,
  useGlobalMasterSearchShortcut,
} from '../../hooks/useGlobalMasterSearchShortcut';
import { useApi } from '../../lib/api';
import { fetchJson } from '../../lib/apiFetchJson';
import {
  STOP_IMPERSONATING,
  describeImpersonationError,
  useSubmitImpersonationMapping,
  useSwitchImpersonation,
} from '../../hooks/useImpersonation';
import { AUTOFILL_IGNORE_PROPS } from '../../lib/autofill';
import {
  readRecentUsers,
  type RecentImpersonationUser,
} from '../../lib/impersonation-recents';
import { emailToDisplayName, getInitials } from '../../view-system/utils/displayFormatters';
import { DemoOptionRow } from '../demo/DemoOptionRow';
import { searchRank } from '../demo/searchRank';
import { filterDemoOptions } from '../demo/useDemoOptions';
import { useDemoStartAction } from '../demo/useDemoStartAction';
import { pushEscapeLayer } from '../ui/escape-stack';
import { parseDomainLink } from '../flow/links';
import { useDebouncedValue } from '../flow/useDebouncedValue';
import { readStoredJson, tolerantArray } from '../../lib/readStoredJson';

/** How long the query must sit still before the search request fires. */
export const MASTER_SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const ALL_GROUP_LIMIT = 5;
const RECENT_SEARCH_KEY = 'terra:master-search:recent';
const MAX_RECENT_SEARCHES = 15;
const DEFAULT_IMPERSONATION_LIMIT = 5;

const ADMIN_TAB_LABELS = { impersonate: 'Impersonate', demo: 'Demo' } as const;
type AdminTab = keyof typeof ADMIN_TAB_LABELS;
type SearchTab = 'all' | 'people' | 'accounts' | AdminTab;
type SearchItem =
  | { kind: 'account'; row: AccountSearchResult }
  | { kind: 'contact'; row: ContactSearchResult }
  | { kind: 'impersonate'; row: OwnerOption }
  | { kind: 'demo'; row: DemoOption };
type VisibleGroup = {
  label: string;
  tab: SearchTab;
  items: SearchItem[];
  total?: number;
  state?: 'loading' | 'error';
};

const RecentSearchItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('account'),
    row: AccountSearchResultSchema,
    visibilityKey: z.string(),
  }),
  z.object({
    kind: z.literal('contact'),
    row: ContactSearchResultSchema,
    visibilityKey: z.string(),
  }),
]);
type RecentSearchItem = z.infer<typeof RecentSearchItemSchema>;

const BASE_TABS: ReadonlyArray<{ id: SearchTab; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'people', label: 'People' },
  { id: 'accounts', label: 'Accounts' },
];

export function readStoredArray<T>(key: string, schema: z.ZodType<T>): T[] {
  return readStoredJson(localStorage, key, tolerantArray(schema), []);
}

function writeStoredArray<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Recents are best-effort. Navigation and identity switching must still
    // succeed when storage is blocked or full.
  }
}

function isAdminTab(tab: SearchTab): tab is AdminTab {
  return tab in ADMIN_TAB_LABELS;
}

function demoItemsMatching(options: readonly DemoOption[], query: string): SearchItem[] {
  return filterDemoOptions(options, query)
    .map((row): SearchItem => ({ kind: 'demo', row }));
}

function recentSearchKey(item: RecentSearchItem): string {
  return `${item.visibilityKey}:${item.kind}:${item.row.id}`;
}

function roleLabel(userType: string | null): string {
  // Reuse the canonical label table (@shared/user-types) rather than a parallel
  // switch — an unknown/null type falls back to the Member label.
  if (userType !== null && userType in USER_TYPE_INFO) {
    return USER_TYPE_INFO[userType as keyof typeof USER_TYPE_INFO].shortLabel;
  }
  return USER_TYPE_INFO.MEMBER.shortLabel;
}

function accountMatchesQuery(row: AccountSearchResult, query: string): boolean {
  return searchRank(
    [row.name, parseDomainLink(row.email_domain_link)?.label, row.industry]
      .filter((value): value is string => Boolean(value))
      .join(' '),
    query,
  ) >= 0;
}

function contactMatchesQuery(row: ContactSearchResult, query: string): boolean {
  return searchRank(
    [row.name, row.email, row.title, row.account_name]
      .filter((value): value is string => Boolean(value))
      .join(' '),
    query,
  ) >= 0;
}

function useFlowSearch(
  query: string,
  scope: FlowSearchScope,
  enabled: boolean,
) {
  const { apiFetch } = useApi();
  return useQuery<FlowSearchResponse>({
    queryKey: ['global-master-search', query, scope],
    enabled: enabled && query.length >= MIN_QUERY_LENGTH,
    // Deliberately NO keepPreviousData: holding the prior query's rows while a
    // new query is in flight leaves stale, mismatched options selectable by
    // Enter. The 250ms debounce already prevents per-keystroke refetch churn.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const params = new URLSearchParams({ q: query, scope });
      return fetchJson(apiFetch, FlowSearchResponseSchema, `/api/flow/search?${params.toString()}`, {
        timeoutMs: 30_000,
      });
    },
  });
}

function GroupHeading({
  children,
  moreLabel,
  onMore,
}: {
  children: string;
  moreLabel?: string;
  onMore?: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 pb-1 pt-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-content-tertiary">
        {children}
      </h2>
      {moreLabel && onMore ? (
        <button
          type="button"
          onClick={onMore}
          className="rounded-md px-2 py-1 text-xs font-medium text-content-action hover:bg-bg-secondary"
        >
          {moreLabel}
        </button>
      ) : null}
    </div>
  );
}

export default function GlobalMasterSearchPalette({
  reloadCurrentPage = () => window.location.reload(),
}: {
  reloadCurrentPage?: () => void;
}) {
  const { apiFetch } = useApi();
  const submitMapping = useSubmitImpersonationMapping();
  const switchImpersonation = useSwitchImpersonation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin, canImpersonate, canStartDemo, userId, email } = useUserControlsContext();
  const mayImpersonate = canImpersonate || canStartDemo;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<SearchTab>('all');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSwitching, setIsSwitching] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>(
    () => readStoredArray(RECENT_SEARCH_KEY, RecentSearchItemSchema),
  );
  const [recentImpersonations, setRecentImpersonations] = useState<RecentImpersonationUser[]>(
    () => readRecentUsers(),
  );
  const searchRef = useRef<HTMLInputElement>(null);

  const excluded = isDelphiRoute(pathname);
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(trimmedQuery, MASTER_SEARCH_DEBOUNCE_MS);
  const scope: FlowSearchScope = isAdmin ? 'all' : 'my';
  const recentVisibilityKey = isAdmin
    ? 'all'
    : userId != null
      ? `user:${userId}`
      : `email:${email?.trim().toLowerCase() ?? 'unknown'}`;
  const adminTabs = useMemo<AdminTab[]>(
    () => [
      ...(canImpersonate ? ['impersonate' as const] : []),
      ...(canStartDemo ? ['demo' as const] : []),
    ],
    [canImpersonate, canStartDemo],
  );
  const tabs = useMemo(
    () => [...BASE_TABS, ...adminTabs.map((id) => ({ id, label: ADMIN_TAB_LABELS[id] }))],
    [adminTabs],
  );

  const openPalette = useCallback(() => {
    setOpen(true);
    // If already open, the ref exists and this repeated chord reclaims focus.
    // On first open, the modal lifecycle effect focuses after the commit.
    searchRef.current?.focus();
  }, []);
  useGlobalMasterSearchShortcut(openPalette);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveTab('all');
    setSelectedIndex(0);
    setActionError(null);
  }, []);
  const startDemoAs = useDemoStartAction({ onError: setActionError, beforeNavigate: close });

  useEffect(() => {
    if (!open || excluded) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    searchRef.current?.focus();

    // Escape is owned by the shared escape-stack (components/ui/escape-stack.ts)
    // so a Cmd/Ctrl+K palette opened over a Drawer/popover closes only itself,
    // not the layer beneath it — a raw document listener would fire both.
    const removeEscapeLayer = pushEscapeLayer(close);
    return () => {
      removeEscapeLayer();
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [close, excluded, open]);

  useEffect(() => {
    if (excluded && open) close();
  }, [close, excluded, open]);

  const flow = useFlowSearch(
    debouncedQuery,
    scope,
    open && !excluded && !isAdminTab(activeTab),
  );
  const admin = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: async () => {
      const response = await fetchAdminMe(apiFetch);
      const parsed = AdminMeResponseSchema.safeParse(response);
      if (!parsed.success) throw new Error('Impersonation returned an invalid response');
      return parsed.data;
    },
    enabled: mayImpersonate && open && !excluded,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const matchingOwners = useMemo(() => {
    if (debouncedQuery.length < MIN_QUERY_LENGTH) return [];
    return (admin.data?.ownerOptions ?? [])
      .map((owner) => ({
        owner,
        rank: Math.max(
          searchRank(emailToDisplayName(owner.ownerEmail), debouncedQuery),
          searchRank(owner.ownerEmail, debouncedQuery),
        ),
      }))
      .filter(({ rank }) => rank >= 0)
      .sort((left, right) => right.rank - left.rank)
      .map(({ owner }) => owner);
  }, [admin.data?.ownerOptions, debouncedQuery]);

  const accountItems = useMemo<SearchItem[]>(
    () => (flow.data?.accounts ?? []).map((row) => ({ kind: 'account', row })),
    [flow.data?.accounts],
  );
  const contactItems = useMemo<SearchItem[]>(
    () => (flow.data?.contacts ?? []).map((row) => ({ kind: 'contact', row })),
    [flow.data?.contacts],
  );
  const demoOptions = admin.data?.demoOptions;
  const impersonateItems = useMemo<SearchItem[]>(
    () => matchingOwners.map((row): SearchItem => ({ kind: 'impersonate', row })),
    [matchingOwners],
  );
  const demoItems = useMemo(
    () => demoItemsMatching(demoOptions ?? [], debouncedQuery),
    [demoOptions, debouncedQuery],
  );
  const recentAccountItems = useMemo<SearchItem[]>(
    () => recentSearches
      .filter((item) => item.visibilityKey === recentVisibilityKey)
      .filter((item): item is Extract<RecentSearchItem, { kind: 'account' }> => item.kind === 'account')
      .map((item) => ({ kind: 'account', row: item.row })),
    [recentSearches, recentVisibilityKey],
  );
  const recentContactItems = useMemo<SearchItem[]>(
    () => recentSearches
      .filter((item) => item.visibilityKey === recentVisibilityKey)
      .filter((item): item is Extract<RecentSearchItem, { kind: 'contact' }> => item.kind === 'contact')
      .map((item) => ({ kind: 'contact', row: item.row })),
    [recentSearches, recentVisibilityKey],
  );
  const recentImpersonateItems = useMemo<SearchItem[]>(
    () => recentImpersonations
      .map((recent) => admin.data?.ownerOptions.find((owner) => (
        owner.slackUserId === recent.slackUserId || owner.ownerEmail === recent.ownerEmail
      )))
      .filter((owner): owner is OwnerOption => owner !== undefined)
      .map((row) => ({ kind: 'impersonate', row })),
    [admin.data?.ownerOptions, recentImpersonations],
  );

  const defaultAccounts = useQuery({
    queryKey: ['global-master-search', 'default-accounts'],
    enabled: isAdmin && open && !excluded
      && trimmedQuery.length < MIN_QUERY_LENGTH
      && recentAccountItems.length === 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const data = await fetchJson(
        apiFetch,
        AccountsResponseSchema,
        '/api/flow/accounts?sort=propensity_score&dir=DESC&group_logic=AND',
        { timeoutMs: 30_000 },
      );
      return data.rows.map<AccountSearchResult>((row) => ({
        id: row.id,
        name: row.name,
        email_domain_link: row.email_domain_link,
        propensity_score: row.propensity_score,
        industry: row.industry,
      }));
    },
  });
  const defaultContacts = useQuery({
    queryKey: ['global-master-search', 'default-contacts'],
    enabled: isAdmin && open && !excluded
      && trimmedQuery.length < MIN_QUERY_LENGTH
      && recentContactItems.length === 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const data = await fetchJson(
        apiFetch,
        ContactsResponseSchema,
        '/api/flow/contacts?sort=last_activity&dir=DESC&group_logic=AND',
        { timeoutMs: 30_000 },
      );
      return data.rows.map<ContactSearchResult>((row) => ({
        id: row.id,
        account_id: row.account_id,
        name: row.name,
        email: row.email,
        title: row.title,
        account_name: row.account_name,
      }));
    },
  });
  const defaultAccountItems = useMemo<SearchItem[]>(
    () => (defaultAccounts.data ?? []).map((row) => ({ kind: 'account', row })),
    [defaultAccounts.data],
  );
  const defaultContactItems = useMemo<SearchItem[]>(
    () => (defaultContacts.data ?? []).map((row) => ({ kind: 'contact', row })),
    [defaultContacts.data],
  );

  const visibleGroups = useMemo<VisibleGroup[]>(() => {
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      const accountState = recentAccountItems.length > 0
        ? undefined
        : defaultAccounts.isLoading
          ? 'loading' as const
          : defaultAccounts.isError
            ? 'error' as const
            : undefined;
      const contactState = recentContactItems.length > 0
        ? undefined
        : defaultContacts.isLoading
          ? 'loading' as const
          : defaultContacts.isError
            ? 'error' as const
            : undefined;
      const adminState = admin.isLoading
        ? 'loading' as const
        : admin.isError
          ? 'error' as const
          : undefined;
      const shortAccountItems = recentAccountItems.length > 0
        ? recentAccountItems
        : defaultAccountItems;
      const shortContactItems = recentContactItems.length > 0
        ? recentContactItems
        : defaultContactItems;
      const locallyFilteredAccounts = trimmedQuery.length === 0
        ? shortAccountItems
        : shortAccountItems.filter((item) => (
          item.kind === 'account' && accountMatchesQuery(item.row, trimmedQuery)
        ));
      const locallyFilteredContacts = trimmedQuery.length === 0
        ? shortContactItems
        : shortContactItems.filter((item) => (
          item.kind === 'contact' && contactMatchesQuery(item.row, trimmedQuery)
        ));
      const locallyFilteredOwners: SearchItem[] = trimmedQuery.length === 0
        ? (recentImpersonateItems.length > 0
          ? recentImpersonateItems
          : (admin.data?.ownerOptions ?? [])
            .slice(0, DEFAULT_IMPERSONATION_LIMIT)
            .map((row): SearchItem => ({ kind: 'impersonate', row })))
        : (admin.data?.ownerOptions ?? [])
          .filter((owner) => (
            Math.max(
              searchRank(emailToDisplayName(owner.ownerEmail), trimmedQuery),
              searchRank(owner.ownerEmail, trimmedQuery),
            ) >= 0
          ))
          .map((row): SearchItem => ({ kind: 'impersonate', row }));
      const shortAdminItems: Record<AdminTab, SearchItem[]> = {
        impersonate: locallyFilteredOwners,
        demo: demoItemsMatching(demoOptions ?? [], trimmedQuery),
      };
      if (activeTab === 'people') {
        return [{ label: 'People', tab: 'people' as const, items: locallyFilteredContacts, state: contactState }];
      }
      if (activeTab === 'accounts') {
        return [{ label: 'Accounts', tab: 'accounts' as const, items: locallyFilteredAccounts, state: accountState }];
      }
      if (isAdminTab(activeTab)) {
        return [{ label: ADMIN_TAB_LABELS[activeTab], tab: activeTab, items: shortAdminItems[activeTab], state: adminState }];
      }
      return [
        { label: 'Accounts', tab: 'accounts' as const, items: locallyFilteredAccounts.slice(0, ALL_GROUP_LIMIT), state: accountState },
        { label: 'People', tab: 'people' as const, items: locallyFilteredContacts.slice(0, ALL_GROUP_LIMIT), state: contactState },
        ...adminTabs.map((tab) => ({ label: ADMIN_TAB_LABELS[tab], tab, items: shortAdminItems[tab].slice(0, ALL_GROUP_LIMIT), state: adminState })),
      ];
    }
    if (debouncedQuery.length < MIN_QUERY_LENGTH) return [];
    if (activeTab === 'people') return [{ label: 'People', tab: 'people' as const, items: contactItems }];
    if (activeTab === 'accounts') return [{ label: 'Accounts', tab: 'accounts' as const, items: accountItems }];
    const adminItems: Record<AdminTab, SearchItem[]> = { impersonate: impersonateItems, demo: demoItems };
    if (isAdminTab(activeTab)) {
      return [{ label: ADMIN_TAB_LABELS[activeTab], tab: activeTab, items: adminItems[activeTab] }];
    }
    return [
      { label: 'Accounts', tab: 'accounts' as const, items: accountItems.slice(0, ALL_GROUP_LIMIT), total: accountItems.length },
      { label: 'People', tab: 'people' as const, items: contactItems.slice(0, ALL_GROUP_LIMIT), total: contactItems.length },
      ...adminTabs.map((tab) => ({ label: ADMIN_TAB_LABELS[tab], tab, items: adminItems[tab].slice(0, ALL_GROUP_LIMIT), total: adminItems[tab].length })),
    ];
  }, [
    accountItems,
    activeTab,
    admin.data?.ownerOptions,
    adminTabs,
    demoItems,
    demoOptions,
    admin.isError,
    admin.isLoading,
    contactItems,
    debouncedQuery.length,
    defaultAccountItems,
    defaultAccounts.isError,
    defaultAccounts.isLoading,
    defaultContactItems,
    defaultContacts.isError,
    defaultContacts.isLoading,
    impersonateItems,
    recentAccountItems,
    recentContactItems,
    recentImpersonateItems,
    trimmedQuery,
  ]);
  const visibleItems = useMemo(
    () => visibleGroups.flatMap((group) => group.items),
    [visibleGroups],
  );
  const activeIndex = visibleItems.length === 0
    ? -1
    : Math.min(selectedIndex, visibleItems.length - 1);

  const setTab = (tab: SearchTab) => {
    setActiveTab(tab);
    setSelectedIndex(0);
    searchRef.current?.focus();
  };

  const isDebouncing = trimmedQuery.length >= MIN_QUERY_LENGTH
    && debouncedQuery !== trimmedQuery;
  const isLoading = trimmedQuery.length >= MIN_QUERY_LENGTH && (
    isDebouncing
    || (isAdminTab(activeTab)
      ? admin.isLoading
      : flow.isLoading)
    || (activeTab === 'all' && adminTabs.length > 0 && admin.isLoading && visibleItems.length === 0)
  );
  const hasError = trimmedQuery.length >= MIN_QUERY_LENGTH
    && (isAdminTab(activeTab) ? admin.isError : flow.isError);

  const rememberSearch = (
    item: Extract<SearchItem, { kind: 'account' | 'contact' }>,
  ) => {
    const recent: RecentSearchItem = { ...item, visibilityKey: recentVisibilityKey };
    const key = recentSearchKey(recent);
    const next = [
      recent,
      ...recentSearches.filter((stored) => recentSearchKey(stored) !== key),
    ].slice(0, MAX_RECENT_SEARCHES);
    setRecentSearches(next);
    writeStoredArray(RECENT_SEARCH_KEY, next);
  };

  const selectItem = (item: SearchItem | undefined) => {
    if (!item || isSwitching) return;
    setActionError(null);
    if (item.kind === 'account') {
      rememberSearch(item);
      navigate(accountInCommandCenter(item.row.id));
      close();
      return;
    }
    if (item.kind === 'contact') {
      rememberSearch(item);
      navigate(item.row.account_id
        ? contactInCommandCenter(item.row.account_id, item.row.id)
        : contactsInCommandCenter(item.row.id));
      close();
      return;
    }

    if (item.kind === 'demo') {
      setIsSwitching(true);
      void startDemoAs(item.row).then((started) => {
        if (!started) setIsSwitching(false);
      });
      return;
    }

    const isSelfImpersonation = item.row.slackUserId === admin.data?.slackUserId
      || item.row.ownerEmail.toLowerCase() === (admin.data?.ownerEmail ?? '').toLowerCase();

    setIsSwitching(true);
    const landed = isSelfImpersonation
      ? submitMapping(STOP_IMPERSONATING)
      : switchImpersonation(item.row).then(() => { setRecentImpersonations(readRecentUsers()); });
    void landed.then(() => {
      close();
      reloadCurrentPage();
    }).catch((error: unknown) => {
      setActionError(describeImpersonationError(error));
      setIsSwitching(false);
    });
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (visibleItems.length === 0) return;
      setSelectedIndex((current) => (
        event.key === 'ArrowDown'
          ? (Math.min(current, visibleItems.length - 1) + 1) % visibleItems.length
          : (Math.min(current, visibleItems.length - 1) - 1 + visibleItems.length) % visibleItems.length
      ));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      selectItem(visibleItems[activeIndex]);
    }
  };

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    event.preventDefault();
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    const delta = event.shiftKey ? -1 : 1;
    const nextIndex = (currentIndex + delta + tabs.length) % tabs.length;
    setTab(tabs[nextIndex].id);
  };

  if (!open || excluded) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div
        aria-hidden="true"
        data-testid="global-master-search-backdrop"
        className="absolute inset-0 bg-bg-scrim"
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search Terra"
        onKeyDown={handleDialogKeyDown}
        className="relative z-10 flex max-h-[min(44rem,calc(100vh-2rem))] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-primary bg-bg-raised shadow-xl"
      >
        <div className="flex items-center gap-3 border-b border-secondary px-4 py-3">
          <Search className="h-5 w-5 shrink-0 text-content-action" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            {...AUTOFILL_IGNORE_PROPS}
            aria-label="Search Terra"
            aria-controls="global-master-search-results"
            aria-activedescendant={activeIndex >= 0 ? `global-master-search-option-${activeIndex}` : undefined}
            placeholder="Search people, companies, or users…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleSearchKeyDown}
            className="min-w-0 flex-1 bg-transparent text-base text-content-primary outline-none placeholder:text-content-placeholder"
          />
          <span className="hidden rounded-md border border-secondary bg-bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-content-tertiary sm:inline">
            ESC
          </span>
          <button
            type="button"
            aria-label="Close Terra search"
            onClick={close}
            className="rounded-md p-1.5 text-content-tertiary hover:bg-bg-secondary hover:text-content-primary"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div role="tablist" aria-label="Search categories" className="flex items-center gap-1 border-b border-secondary px-4 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls="global-master-search-results"
              onClick={() => setTab(tab.id)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                activeTab === tab.id
                  ? 'bg-bg-selected text-content-selected'
                  : 'text-content-secondary hover:bg-bg-secondary-hover hover:text-content-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
          <span className="ml-auto rounded-full border border-secondary bg-bg-secondary px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-content-tertiary">
            {scope === 'all' ? 'All records' : 'My book'}
          </span>
        </div>

        <div
          id="global-master-search-results"
          role="listbox"
          aria-label="Terra search results"
          className="min-h-52 overflow-y-auto pb-3"
        >
          {isLoading ? (
            <div className="flex min-h-52 items-center justify-center gap-2 text-sm text-content-tertiary">
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
              Searching…
            </div>
          ) : null}
          {hasError ? (
            <div className="flex min-h-52 flex-col items-center justify-center gap-2 px-6 text-center text-content-danger">
              <AlertCircle className="h-5 w-5" aria-hidden="true" />
              <p className="text-sm">Search is unavailable. Try again.</p>
            </div>
          ) : null}
          {actionError ? (
            <div role="alert" className="mx-4 mt-3 rounded-lg border border-border-danger bg-bg-red-light px-3 py-2 text-sm text-content-danger">
              {actionError}
            </div>
          ) : null}
          {trimmedQuery.length >= MIN_QUERY_LENGTH
          && activeTab === 'all'
          && adminTabs.length > 0
          && admin.isError
          && !isLoading ? (
            <div role="status" className="mx-4 mt-3 rounded-lg border border-border-warning bg-bg-orange-light px-3 py-2 text-sm text-content-warning">
              Impersonation results are unavailable.
            </div>
          ) : null}
          {!isLoading && !hasError
            ? visibleGroups.map((group) => {
                const offset = visibleGroups
                  .slice(0, visibleGroups.indexOf(group))
                  .reduce((total, previous) => total + previous.items.length, 0);
                const hasMore = group.total !== undefined && group.total > group.items.length;
                return (
                  <section key={group.label}>
                    <GroupHeading
                      moreLabel={hasMore ? `More ${group.label.toLowerCase()} — filter to ${group.label}` : undefined}
                      onMore={hasMore ? () => setTab(group.tab) : undefined}
                    >
                      {group.label}
                    </GroupHeading>
                    {group.state === 'loading' ? (
                      <p className="flex items-center gap-2 px-4 py-2 text-xs text-content-tertiary">
                        <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        Loading {group.label.toLowerCase()}…
                      </p>
                    ) : group.state === 'error' ? (
                      <p className="px-4 py-2 text-xs text-content-danger">
                        {group.label} unavailable.
                      </p>
                    ) : group.items.length === 0 ? (
                      <p className="px-4 py-2 text-xs text-content-tertiary">
                        No {group.label.toLowerCase()} to show.
                      </p>
                    ) : null}
                    {group.items.map((item, index) => {
                      const itemIndex = offset + index;
                      const selected = activeIndex === itemIndex;
                      if (item.kind === 'account') {
                        const name = item.row.name ?? 'Unnamed account';
                        const domain = parseDomainLink(item.row.email_domain_link)?.label;
                        return (
                          <button
                            key={`account:${item.row.id}`}
                            id={`global-master-search-option-${itemIndex}`}
                            type="button"
                            role="option"
                            aria-label={`Open account ${name}`}
                            aria-selected={selected}
                            disabled={isSwitching}
                            onMouseEnter={() => setSelectedIndex(itemIndex)}
                            onClick={() => selectItem(item)}
                            className={`flex w-full items-center gap-3 px-4 py-2.5 text-left ${
                              selected ? 'bg-bg-selected' : 'hover:bg-bg-secondary'
                            }`}
                          >
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-blue text-content-action">
                              <Building2 className="h-4 w-4" aria-hidden="true" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-content-primary">{name}</span>
                              <span className="block truncate text-xs text-content-tertiary">
                                {domain ?? item.row.industry ?? 'Salesforce account'}
                              </span>
                            </span>
                          </button>
                        );
                      }
                      if (item.kind === 'contact') {
                        const name = item.row.name ?? item.row.email ?? 'Unnamed contact';
                        return (
                          <button
                            key={`contact:${item.row.id}`}
                            id={`global-master-search-option-${itemIndex}`}
                            type="button"
                            role="option"
                            aria-label={`Open person ${name}`}
                            aria-selected={selected}
                            disabled={isSwitching}
                            onMouseEnter={() => setSelectedIndex(itemIndex)}
                            onClick={() => selectItem(item)}
                            className={`flex w-full items-center gap-3 px-4 py-2.5 text-left ${
                              selected ? 'bg-bg-selected' : 'hover:bg-bg-secondary'
                            }`}
                          >
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bg-purple text-xs font-semibold text-content-secondary">
                              {item.row.name ? getInitials(item.row.name) : <ContactRound className="h-4 w-4" aria-hidden="true" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-content-primary">{name}</span>
                              <span className="block truncate text-xs text-content-tertiary">
                                {[item.row.title, item.row.account_name, item.row.email].filter(Boolean).join(' · ') || 'Salesforce contact'}
                              </span>
                            </span>
                          </button>
                        );
                      }
                      if (item.kind === 'demo') {
                        return (
                          <DemoOptionRow
                            key={`demo:${item.row.slackUserId}`}
                            id={`global-master-search-option-${itemIndex}`}
                            role="option"
                            aria-selected={selected}
                            selected={selected}
                            option={item.row}
                            disabled={isSwitching}
                            onMouseEnter={() => setSelectedIndex(itemIndex)}
                            onSelect={() => selectItem(item)}
                          />
                        );
                      }
                      const name = emailToDisplayName(item.row.ownerEmail);
                      return (
                        <button
                          key={`impersonate:${item.row.slackUserId}`}
                          id={`global-master-search-option-${itemIndex}`}
                          type="button"
                          role="option"
                          aria-label={`Impersonate ${name} (${item.row.ownerEmail})`}
                          aria-selected={selected}
                          disabled={isSwitching}
                          onMouseEnter={() => setSelectedIndex(itemIndex)}
                          onClick={() => selectItem(item)}
                          className={`flex w-full items-center gap-3 px-4 py-2.5 text-left ${
                            selected ? 'bg-bg-selected' : 'hover:bg-bg-secondary'
                          }`}
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bg-orange text-content-warning">
                            <UserRoundCog className="h-4 w-4" aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-content-primary">{name}</span>
                            <span className="block truncate text-xs text-content-tertiary">{item.row.ownerEmail}</span>
                          </span>
                          <span className="rounded-full border border-secondary bg-bg-secondary px-2 py-0.5 text-[10px] font-semibold text-content-secondary">
                            {roleLabel(item.row.userType)}
                          </span>
                        </button>
                      );
                    })}
                  </section>
                );
              })
            : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
